import { useState } from "react";
import { Copy, Check, Mail, Phone, MapPin, Globe, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./common";
import { TChapter, useLang, t } from "../i18n";
import { CONTACT, SCHOLAR_IDS } from "../data/cv";

const CopyLine = ({ icon: Icon, label, value, href, testid, dark = true }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(value);
    setCopied(true);
    toast.success(`${label} copied`, { description: value });
    setTimeout(() => setCopied(false), 1800);
  };
  const inner = (
    <>
      <span className={`w-11 h-11 rounded-full grid place-items-center shrink-0 ${dark ? "bg-card_dark/10 text-saffron" : "bg-void/5 text-crimson"}`}>
        <Icon size={18} />
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block font-mono text-[10px] uppercase tracking-[0.25em] ${dark ? "text-bone/40" : "text-mutedink"}`}>{label}</span>
        <span className={`block font-display text-lg sm:text-xl font-semibold truncate ${dark ? "text-bone" : "text-bone"}`}>{value}</span>
      </span>
    </>
  );
  return (
    <div className={`flex items-center gap-4 rounded-2xl border p-5 transition-colors duration-300 ${dark ? "border-bone/10 bg-card_dark hover:border-saffron/40" : "border-bone/10 bg-white hover:border-crimson/40"}`}>
      {href ? (
        <a data-testid={testid} href={href} className="flex items-center gap-4 flex-1 min-w-0">
          {inner}
        </a>
      ) : (
        <div className="flex items-center gap-4 flex-1 min-w-0">{inner}</div>
      )}
      <button
        data-testid={`${testid}-copy`}
        onClick={copy}
        aria-label={`Copy ${label}`}
        className={`shrink-0 w-10 h-10 rounded-full grid place-items-center border transition-colors ${dark ? "border-bone/15 text-bone/60 hover:text-saffron hover:border-saffron/50" : "border-bone/15 text-mutedink hover:text-crimson hover:border-crimson/50"}`}
      >
        {copied ? <Check size={15} className="text-emerald" /> : <Copy size={15} />}
      </button>
    </div>
  );
};

const Contact = () => (
  <section id="connect" className="relative bg-void text-bone bg-grid-dark py-24 sm:py-36 overflow-hidden" data-testid="section-connect">
    <div className="absolute -top-20 right-0 w-[420px] h-[420px] rounded-full bg-crimson/10 blur-3xl pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
      <TChapter dark num="06" k="c6" />

      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
        <div>
          <Reveal>
            <p className="text-bone/70 text-base sm:text-lg leading-relaxed mb-10">
              {t(useLang().lang, "contact_intro")}
            </p>
          </Reveal>
          <div className="space-y-4">
            <CopyLine icon={Mail} label={t(useLang().lang, "label_email_1")} value={CONTACT.emails[0]} href={`mailto:${CONTACT.emails[0]}`} testid="contact-email-1" />
            <CopyLine icon={Mail} label={t(useLang().lang, "label_email_2")} value={CONTACT.emails[1]} href={`mailto:${CONTACT.emails[1]}`} testid="contact-email-2" />
            <CopyLine icon={Phone} label={t(useLang().lang, "label_phone")} value={CONTACT.phone} href={`tel:${CONTACT.phone}`} testid="contact-phone" />
            <CopyLine icon={Globe} label={t(useLang().lang, "label_web")} value={CONTACT.domain} href="https://www.abhishekverma.pro" testid="contact-domain" />
          </div>
        </div>

        <div className="space-y-8">
          <Reveal className="rounded-2xl border border-bone/10 bg-card_dark p-7 sm:p-8" data-testid="contact-institution-card">
            <MapPin size={20} className="text-crimson mb-4" />
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone/40 mb-2">{t(useLang().lang, "label_address")}</p>
            <p className="font-display text-xl font-semibold leading-snug">{CONTACT.institution}</p>
            <p className="text-bone/60 mt-2">{CONTACT.university}</p>
          </Reveal>

          <Reveal delay={0.1} className="rounded-2xl border border-bone/10 bg-card_dark p-7 sm:p-8" data-testid="contact-scholar-profiles">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone/40 mb-5">{t(useLang().lang, "label_profiles")}</p>
            <div className="grid grid-cols-2 gap-3">
              {SCHOLAR_IDS.map((s) => (
                <a
                  key={s.key}
                  data-testid={`contact-profile-${s.key}`}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-between gap-2 border border-bone/10 rounded-xl px-4 py-3.5 hover:border-saffron/50 hover:bg-card_dark/[0.03] transition-colors"
                >
                  <span>
                    <span className="block font-mono text-[9px] uppercase tracking-[0.2em] text-bone/40">{s.label}</span>
                    <span className="block font-mono text-[11px] text-bone/90 mt-0.5">{s.value}</span>
                  </span>
                  <ArrowUpRight size={14} className="text-bone/30 group-hover:text-saffron group-hover:rotate-45 transition-all shrink-0" />
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </div>

      <div className="mt-24 pt-10 border-t border-bone/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <p className="font-display text-2xl sm:text-3xl font-bold">
          abhishekverma<span className="text-crimson">.</span>pro
        </p>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone/40">
          {t(useLang().lang, "footer_rights")}
        </p>
      </div>
    </div>
  </section>
);

export default Contact;
